import * as z from 'zod';
import { CatalogType, PatientTherapySequenceStatus, PreparationType, VolumeMode } from '../database/schema.js';
import { isInThePast } from './functions.js';
import { getUserPermissions, hasPermission } from './permissions.js';
// Which Activity statuses a permission puts on the user's list (Permissions plan, section 6.6).
export const PermissionToSequenceStatusesMap = {
    'prescriptions.approve_budget': [PatientTherapySequenceStatus.enum.pending_approval],
    'prescriptions.validate_doctor': [
        PatientTherapySequenceStatus.enum.pending_doctor_validation,
        PatientTherapySequenceStatus.enum.validating,
    ],
    'prescriptions.validate_pharmacist': [PatientTherapySequenceStatus.enum.validating],
    'prescriptions.patient_arrival': [PatientTherapySequenceStatus.enum.validating],
    'production.produce': [
        PatientTherapySequenceStatus.enum.production_planning,
        PatientTherapySequenceStatus.enum.in_production,
    ],
    'production.release': [PatientTherapySequenceStatus.enum.qa_dispatch, PatientTherapySequenceStatus.enum.returned],
    'administration.administer': [
        PatientTherapySequenceStatus.enum.released_for_delivery,
        PatientTherapySequenceStatus.enum.administration,
    ],
};
export function determinePreparationType(sequence, settings) {
    if (settings?.useScales === false)
        return PreparationType.enum.VOLUMETRIC;
    return sequence?.preparationType ?? PreparationType.enum.GRAVIMETRIC;
}
export function therapySequenceIncludesActiveSubstance(sequence) {
    return Boolean(sequence?.medicine?.some(m => {
        const catalogType = m.activeSubstance?.catalogType || m.prescribedName?.drugCatalogItem?.catalogType;
        return catalogType === CatalogType.enum.ACTIVE_SUBSTANCE;
    }));
}
export function therapySequenceRequiresAdditionalApproval(medicines) {
    return Boolean(medicines?.some(m => m.activeSubstance?.activeSubstanceDetails?.additionalApprovalRequired ||
        m.prescribedName?.drugCatalogItem?.activeSubstanceDetails?.additionalApprovalRequired));
}
/**
 * The carrier fields shared by the prescription form, the save mutators and both validations:
 * a carrier needs a method and a positive volume. `requireCarrierSolution` comes from the selected
 * application form, so the form can also demand a carrier that has not been picked yet.
 */
export const CarrierSolutionValidationSchema = z
    .object({
    requireCarrierSolution: z.boolean().optional(),
    carrierSolutionId: z.string().nullable().optional(),
    volume: z.number().nullable().optional(),
    volumeMode: VolumeMode.nullable().optional(),
})
    .superRefine((value, ctx) => {
    const carrierSelected = Boolean(value.carrierSolutionId);
    if (!value.requireCarrierSolution && !carrierSelected)
        return;
    if (value.requireCarrierSolution && !carrierSelected) {
        ctx.addIssue({ code: 'custom', path: ['carrierSolutionId'], message: 'carrier_solution_required' });
    }
    if (!(typeof value.volume === 'number' && value.volume > 0)) {
        ctx.addIssue({ code: 'custom', path: ['volume'], message: 'volume_required' });
    }
    if (!value.volumeMode) {
        ctx.addIssue({ code: 'custom', path: ['volumeMode'], message: 'volume_mode_required' });
    }
});
export function getTherapySequenceInitialStatus(requiresAdditionalApproval) {
    if (requiresAdditionalApproval)
        return PatientTherapySequenceStatus.enum.pending_approval;
    return PatientTherapySequenceStatus.enum.pending_doctor_validation;
}
function formatMedicineAmount(amount, precisionDecimals) {
    return precisionDecimals != null ? amount.toFixed(precisionDecimals) : String(amount);
}
export function buildProductName(sequence) {
    return (sequence?.medicine
        ?.map(m => {
        const name = m.prescribedName?.name ?? m.activeSubstance?.name ?? '';
        if (!name)
            return '';
        const unit = m.activeSubstance?.activeSubstanceDetails?.doseUnit?.ucumCode;
        const amount = m.totalAmount != null ? formatMedicineAmount(m.totalAmount, m.precisionDecimals) : null;
        return amount ? `${name} ${amount}${unit ? ` ${unit}` : ''}` : name;
    })
        .filter(Boolean)
        .join(', ') || '');
}
export function getApplicableSequenceStatusesForGroups(groups) {
    const permissions = getUserPermissions(groups);
    const statuses = new Set();
    for (const [permission, permissionStatuses] of Object.entries(PermissionToSequenceStatusesMap)) {
        if (!permissions.has(permission))
            continue;
        for (const status of permissionStatuses)
            statuses.add(status);
    }
    return [...statuses];
}
const PRE_PRODUCTION_STATUSES = new Set([
    PatientTherapySequenceStatus.enum.pending_approval,
    PatientTherapySequenceStatus.enum.pending_doctor_validation,
    PatientTherapySequenceStatus.enum.validating,
    PatientTherapySequenceStatus.enum.production_planning,
]);
const NOT_VALIDATED_STATUSES = new Set([
    PatientTherapySequenceStatus.enum.pending_approval,
    PatientTherapySequenceStatus.enum.pending_doctor_validation,
    PatientTherapySequenceStatus.enum.cancelled,
]);
/** Also true without `doctorValidatedAt` when a validation bypass moved the prescription past doctor validation. */
export function isSequenceDoctorValidated(sequence) {
    return sequence.doctorValidatedAt != null || (!!sequence.status && !NOT_VALIDATED_STATUSES.has(sequence.status));
}
/** Whether a prescription has not started physical compounding yet (so it can still be cancelled). */
export function isSequenceBeforeProduction(sequence) {
    return !sequence.status || PRE_PRODUCTION_STATUSES.has(sequence.status);
}
/** Statuses where the unit is produced and handed over (production number / use-by apply). */
const POST_PRODUCTION_STATUSES = new Set([
    PatientTherapySequenceStatus.enum.released_for_delivery,
    PatientTherapySequenceStatus.enum.administration,
    PatientTherapySequenceStatus.enum.administered,
    PatientTherapySequenceStatus.enum.returned,
    PatientTherapySequenceStatus.enum.discarded,
]);
/** Whether the unit was handed over: production number and use-by apply. */
export function isPostProduction(sequence) {
    return Boolean(sequence?.status && POST_PRODUCTION_STATUSES.has(sequence.status));
}
/** Whether production is finished, so the prescription's documents can be reprinted (CY-87). */
export function hasReachedProduction(sequence) {
    return isPostProduction(sequence) || sequence.status === PatientTherapySequenceStatus.enum.qa_dispatch;
}
/** Whether the preparation is compounded purely volumetrically. */
export function isVolumetricPreparation(preparationType) {
    return preparationType === PreparationType.enum.VOLUMETRIC;
}
/** Whether the preparation was compounded by weight, so it has scale readings to report on. */
export function isWeighedPreparation(preparationType) {
    return Boolean(preparationType) && !isVolumetricPreparation(preparationType);
}
export function getPrescriptionRoles(groups) {
    return {
        canEdit: hasPermission(groups, 'prescriptions.edit'),
        canValidateDoctor: hasPermission(groups, 'prescriptions.validate_doctor'),
        canValidatePharmacist: hasPermission(groups, 'prescriptions.validate_pharmacist'),
        canRelease: hasPermission(groups, 'production.release'),
    };
}
/** Anyone allowed to edit prescriptions may add one to a patient's therapy. */
export function canAddPatientTherapy(roles) {
    return roles.canEdit;
}
/**
 * A doctor validated the clinical content, so only a doctor may change it again.
 * Pharmacists may still edit what production needs (trade name, carrier, volume and notes).
 */
export function canEditValidatedPrescription(roles) {
    return roles.canValidateDoctor;
}
// Statuses where a validation can still be added or removed (production has not started).
const VALIDATION_MUTABLE_STATUSES = new Set([
    PatientTherapySequenceStatus.enum.validating,
    PatientTherapySequenceStatus.enum.production_planning,
]);
export function isValidationMutable(sequence) {
    return !!sequence.status && VALIDATION_MUTABLE_STATUSES.has(sequence.status);
}
/** Creating or editing a prescription needs prescriptions.edit; other roles view it read-only. */
export function canEditPatientTherapy(roles) {
    return roles.canEdit;
}
/** A past prescription is read-only, and a validation can only be removed before production starts. */
export function canRemovePatientTherapyValidation(sequence, validation, roles) {
    if (isInThePast(sequence.therapyDate) || !isValidationMutable(sequence))
        return false;
    if (validation === 'pharmacist')
        return Boolean(sequence.pharmacistValidatedAt) && roles.canValidatePharmacist;
    if (validation === 'doctor')
        return Boolean(sequence.doctorValidatedAt) && roles.canValidateDoctor;
    return sequence.pharmacistValidatedAt ? roles.canValidatePharmacist : roles.canValidateDoctor;
}
// A finished preparation must still be released, rejected, administered or returned after its therapy date (CY-325).
const PAST_DATE_ALLOWED_STATUSES = new Set([
    PatientTherapySequenceStatus.enum.qa_dispatch,
    PatientTherapySequenceStatus.enum.released_for_delivery,
]);
/** Whether the sequence's actions are disabled because its therapy date has passed (steps up to production). */
export function isBlockedByPastTherapyDate(sequence) {
    if (!isInThePast(sequence.therapyDate))
        return false;
    return !sequence.status || !PAST_DATE_ALLOWED_STATUSES.has(sequence.status);
}
/** Whether the therapy date has passed but the finished preparation may still be processed, with a warning. */
export function hasPassedTherapyDate(sequence) {
    return isInThePast(sequence.therapyDate) && !isBlockedByPastTherapyDate(sequence);
}
/** A discarded prescription can be remade once, by someone allowed to release production. */
export function canStartPatientTherapyRemake(sequence, roles) {
    return sequence.status === PatientTherapySequenceStatus.enum.discarded && !sequence.remadeAt && roles.canRelease;
}
//# sourceMappingURL=patients-therapies.js.map